import { buttonVariants } from "@foreday/ui/components/button-variants";
import {
  CaretDownIcon,
  CaretLeftIcon,
  CaretUpIcon,
} from "@foreday/ui/components/icons";
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { DocumentSkeleton } from "@/client/components/document-skeleton";
import {
  HeldButton,
  HeldMenu,
  HeldTitle,
} from "@/client/components/page-pending";
import { ScreenHeading } from "@/client/components/screen-heading";
import { ApiError } from "@/client/lib/api";
import { pageTitle } from "@/client/lib/page-title";
import { PENDING_MS } from "@/client/lib/pending";
import {
  ensureTenantData,
  requireModule,
  useModuleOn,
} from "@/client/lib/session";
import { dayIn } from "@/features/attendance/rules";
import { DownloadButton } from "@/features/payslips/components/download-button";
import {
  PayslipDetail,
  SlipDetailLayout,
  slipTitle,
} from "@/features/payslips/components/payslip-detail";
import { PayslipPreview } from "@/features/payslips/components/payslip-preview";
import { SlipEditorSkeleton } from "@/features/payslips/components/slip-editor-skeleton";
import {
  SlipLayout,
  SlipPage,
  slipLine,
} from "@/features/payslips/components/slip-page";
import { payslipsCopy } from "@/features/payslips/copy";
import { payslipsSearch } from "@/features/payslips/month-search";
import {
  documentQuery,
  monthSlipsQuery,
  mySlipsQuery,
  openMonth,
} from "@/features/payslips/payslips-query";
import type { Payslip } from "@/features/payslips/schemas";

const back = buttonVariants({ variant: "ghost", size: "sm" });

export const Route = createFileRoute(
  "/_authenticated/$slug/payslips/$payslipId",
)({
  // An Admin reaches a slip from its month, which the address carries, or their own from My
  // payslips without one; anybody else from their own.
  validateSearch: payslipsSearch,
  loaderDeps: ({ search }) => ({ month: search.payMonth }),
  beforeLoad: ({ context }) => requireModule(context, "payslips"),
  // The page beside the slip loads with it, so it is drawn in the first frame, not a grey box.
  loader: async ({ context, deps, params }) => {
    // A prefetch never rejects: a page that fails stays a skeleton and the slip still opens.
    const page = () =>
      context.queryClient.prefetchQuery(documentQuery(params.payslipId));
    if (context.role !== "admin") {
      await Promise.all([
        ensureTenantData(context.queryClient, mySlipsQuery),
        page(),
      ]);
    } else if (deps.month) {
      await Promise.all([
        openMonth(context.queryClient, context, deps.month),
        page(),
      ]);
    } else {
      const [mine] = await Promise.all([
        ensureTenantData(context.queryClient, mySlipsQuery),
        page(),
      ]);
      if (mine.some((slip) => slip.id === params.payslipId)) return;
      // Anybody else's slip opens through its month; one that is not there stays Not found, and any
      // other failure is the route's error.
      const found = await ensureTenantData(
        context.queryClient,
        documentQuery(params.payslipId),
      ).catch((failure: unknown) => {
        if (
          failure instanceof ApiError &&
          (failure.code === "NOT_FOUND" || failure.code === "VALIDATION_FAILED")
        ) {
          return null;
        }
        throw failure;
      });
      if (found) {
        throw redirect({
          to: "/$slug/payslips/$payslipId",
          params,
          search: { payMonth: found.month },
          replace: true,
        });
      }
    }
  },
  head: async ({ match }) => {
    const { payslipsCopy } = await import("@/features/payslips/copy");
    return {
      meta: [
        { title: pageTitle(payslipsCopy.title, match.context.companyName) },
      ],
    };
  },
  pendingMs: PENDING_MS,
  pendingComponent: SlipPending,
  component: Slip,
});

function Slip() {
  const { role } = Route.useRouteContext();
  const { payMonth: month } = Route.useSearch();
  if (role === "admin" && month) return <AdminSlip month={month} />;
  return <OwnSlip />;
}

function AdminSlip({ month }: { month: string }) {
  const { slug, payslipId } = Route.useParams();
  const { companyTimezone } = Route.useRouteContext();
  const attendanceOn = useModuleOn("attendance");
  const { data } = useSuspenseQuery(monthSlipsQuery(month));
  const slip = data.items.find((one) => one.id === payslipId);
  if (!slip) return <NotFound />;
  return (
    <SlipPage
      slug={slug}
      slip={slip}
      data={data}
      today={dayIn(companyTimezone, new Date())}
      timeZone={companyTimezone}
      attendanceOn={attendanceOn}
      back={<Back />}
    />
  );
}

function OwnSlip() {
  const { slug, payslipId } = Route.useParams();
  const { companyTimezone } = Route.useRouteContext();
  const attendanceOn = useModuleOn("attendance");
  const { data } = useSuspenseQuery(mySlipsQuery);
  const slip = data.find((one) => one.id === payslipId);
  if (!slip) return <NotFound />;
  return (
    <PayslipDetail
      slip={slip}
      timeZone={companyTimezone}
      back={<Back />}
      attendance={
        attendanceOn ? <AttendanceLink slug={slug} slip={slip} /> : null
      }
      preview={
        <PayslipPreview
          id={slip.id}
          label={payslipsCopy.slip.page(slipTitle(slip))}
        />
      }
    />
  );
}

/**
 * To the month an Admin came from, to an Admin's own slips, or to anybody else's. The slip is under
 * the page it goes back to, so only an exact match would mark it current.
 */
function Back() {
  const { slug } = Route.useParams();
  const { role } = Route.useRouteContext();
  const { payMonth: month } = Route.useSearch();
  if (role === "admin" && month) {
    return (
      <Link
        to="/$slug/payslips"
        params={{ slug }}
        search={{ payMonth: month }}
        activeOptions={{ exact: true }}
        className={back}
      >
        <CaretLeftIcon aria-hidden="true" />
        {payslipsCopy.slip.back}
      </Link>
    );
  }
  return role === "admin" ? (
    <Link
      to="/$slug/payslips/mine"
      params={{ slug }}
      activeOptions={{ exact: true }}
      className={back}
    >
      <CaretLeftIcon aria-hidden="true" />
      {payslipsCopy.mine.title}
    </Link>
  ) : (
    <Link
      to="/$slug/payslips"
      params={{ slug }}
      activeOptions={{ exact: true }}
      className={back}
    >
      <CaretLeftIcon aria-hidden="true" />
      {payslipsCopy.title}
    </Link>
  );
}

/**
 * A slip's first load, laid out as the slip draws it: an Admin's from its month in the editor, and
 * one's own under its page. The name or month comes from the slips already read, when they are.
 */
function SlipPending() {
  const { payslipId } = Route.useParams();
  const { role, companyTimezone } = Route.useRouteContext();
  const { payMonth: month } = Route.useSearch();
  const queryClient = useQueryClient();
  const status = <HeldTitle className="text-body" lines={["w-40"]} />;
  if (role === "admin" && month) {
    const slip = queryClient
      .getQueryData(monthSlipsQuery(month).queryKey)
      ?.items.find((one) => one.id === payslipId);
    return (
      <SlipLayout
        busy
        back={<Back />}
        title={
          slip ? (
            <ScreenHeading className="truncate font-display text-heading text-foreground">
              {slip.name}
            </ScreenHeading>
          ) : (
            <HeldTitle className="text-heading" lines={["w-40"]} />
          )
        }
        line={
          slip ? (
            <p className="text-body text-muted-foreground">
              {slipLine(slip, companyTimezone)}
            </p>
          ) : (
            status
          )
        }
        actions={
          <>
            {slip?.status === "finalised" ? (
              <>
                <HeldButton>{payslipsCopy.slip.download}</HeldButton>
                <HeldMenu />
              </>
            ) : slip?.supplementary ? (
              <>
                <HeldButton>{payslipsCopy.slip.publish}</HeldButton>
                <HeldMenu />
              </>
            ) : null}
            <HeldTitle className="text-body" lines={["w-12"]} />
            <HeldButton className="w-[var(--size-touch)] px-0">
              <CaretUpIcon />
            </HeldButton>
            <HeldButton className="w-[var(--size-touch)] px-0">
              <CaretDownIcon />
            </HeldButton>
          </>
        }
        form={<SlipEditorSkeleton />}
        below={
          <HeldButton className="w-full">
            {payslipsCopy.slip.preview}
          </HeldButton>
        }
        preview={<DocumentSkeleton />}
      />
    );
  }
  const own = queryClient
    .getQueryData(mySlipsQuery.queryKey)
    ?.find((one) => one.id === payslipId);
  return (
    <SlipDetailLayout
      busy
      back={<Back />}
      title={
        own ? (
          <ScreenHeading className="text-heading">
            {slipTitle(own)}
          </ScreenHeading>
        ) : (
          <HeldTitle className="text-heading" lines={["w-40"]} />
        )
      }
      published={status}
      download={
        own ? (
          <DownloadButton id={payslipId} month={own.month} />
        ) : (
          <HeldButton>{payslipsCopy.slip.download}</HeldButton>
        )
      }
      preview={<DocumentSkeleton />}
    >
      <div aria-hidden="true" className="flex flex-col gap-1.5 py-1">
        <HeldTitle className="text-body" lines={["w-20"]} />
        <HeldTitle className="font-display text-display" lines={["w-48"]} />
        <HeldTitle className="text-body" lines={["w-56"]} />
      </div>
    </SlipDetailLayout>
  );
}

function AttendanceLink({ slug, slip }: { slug: string; slip: Payslip }) {
  return (
    <Link
      to="/$slug/attendance/my-month"
      params={{ slug }}
      search={{ month: slip.month }}
      className="font-semibold text-primary-text hover:underline"
    >
      {payslipsCopy.slip.openAttendance}
    </Link>
  );
}

/** One Not found for a slip in another company, somebody else's, or none at all. */
function NotFound() {
  const { slug } = Route.useParams();
  return (
    <main className="flex w-full flex-col items-start gap-4 px-6 py-5 lg:px-[var(--pad-panel-x)]">
      <ScreenHeading className="text-heading">
        {payslipsCopy.refused.payslipNotFound}
      </ScreenHeading>
      <Link to="/$slug/payslips" params={{ slug }} className={buttonVariants()}>
        {payslipsCopy.title}
      </Link>
    </main>
  );
}
